import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DataTable } from '@/components/data-table/DataTable'
import { ArrowLeft, Upload, Download, AlertCircle, CheckCircle, Users } from 'lucide-react'
import { useCreateInvoice } from '@/features/invoices/hooks/useInvoices'
import { useClients } from '@/features/clients/hooks/useClients'
import { useCustomers } from '@/features/customers/hooks/useCustomers'
import { supabase } from '@/lib/supabase'
import { invoiceSchema } from '@/features/invoices/schemas/invoiceSchema'
import { formatCurrency } from '@/lib/format'
import { z } from 'zod'

interface ParsedRow {
  client_name: string
  customer_name: string
  invoice_number: string
  invoice_date: string
  due_date: string
  original_amount: string
  status?: string
}

interface ValidationResult {
  id: string
  row: number
  data: ParsedRow
  valid: boolean
  errors: string[]
  clientId?: string
  customerId?: string
}

export function InvoiceImportPage() {
  const navigate = useNavigate()
  const { data: clients } = useClients()
  const { data: customers } = useCustomers()
  const createInvoice = useCreateInvoice()
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ValidationResult[]>([])
  const [importing, setImporting] = useState(false)
  const [importComplete, setImportComplete] = useState(false)
  const [importResults, setImportResults] = useState({ success: 0, failed: 0 })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      parseFile(selectedFile)
    }
  }

  const parseFile = (file: File) => {
    Papa.parse<ParsedRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const validated = results.data.map((row, index) => validateRow(row, index + 2))
        setParsed(validated)
      },
      error: (error) => {
        alert(`CSV parsing error: ${error.message}`)
      },
    })
  }

  const validateRow = (row: ParsedRow, rowNumber: number): ValidationResult => {
    const errors: string[] = []

    // Find client by name
    const client = clients?.find(c => c.name.toLowerCase() === row.client_name?.toLowerCase().trim())
    if (!client) {
      errors.push(`Client "${row.client_name}" not found`)
    }

    // Find customer by name
    const customer = customers?.find(c =>
      c.name.toLowerCase() === row.customer_name?.toLowerCase().trim() &&
      c.client_id === client?.id
    )
    if (!customer && client) {
      errors.push(`Customer "${row.customer_name}" not found for client "${row.client_name}"`)
    }

    // Validate invoice data with Zod
    try {
      invoiceSchema.parse({
        customer_id: customer?.id,
        invoice_number: row.invoice_number,
        invoice_date: row.invoice_date,
        due_date: row.due_date,
        original_amount: parseFloat(row.original_amount),
        status: row.status || 'outstanding',
      })
    } catch (error) {
      if (error instanceof z.ZodError) {
        errors.push(...error.errors.map(e => `${e.path.join('.')}: ${e.message}`))
      }
    }

    return {
      id: `row-${rowNumber}`,
      row: rowNumber,
      data: row,
      valid: errors.length === 0,
      errors,
      clientId: client?.id,
      customerId: customer?.id,
    }
  }

  const handleImport = async () => {
    const validRows = parsed.filter(r => r.valid)
    if (validRows.length === 0) {
      alert('No valid rows to import')
      return
    }

    setImporting(true)
    let success = 0
    let failed = 0

    // Track amounts by client ID
    const clientTotals = new Map<string, number>()

    for (const row of validRows) {
      try {
        await createInvoice.mutateAsync({
          customer_id: row.customerId!,
          client_id: row.clientId!,
          invoice_number: row.data.invoice_number,
          invoice_date: row.data.invoice_date,
          due_date: row.data.due_date,
          original_amount: parseFloat(row.data.original_amount),
          status: row.data.status || 'outstanding',
        })

        // Add to client total
        const amount = parseFloat(row.data.original_amount)
        const currentTotal = clientTotals.get(row.clientId!) || 0
        clientTotals.set(row.clientId!, currentTotal + amount)

        success++
      } catch (error) {
        console.error('Failed to import invoice:', error)
        failed++
      }
    }

    // Update baseline AR for all affected clients
    for (const [clientId, importedTotal] of clientTotals) {
      try {
        console.log(`Updating baseline AR for client ${clientId}`)

        // Get current client data
        const { data: client } = await supabase
          .from('clients')
          .select('baseline_ar_total')
          .eq('id', clientId)
          .single()

        const currentBaseline = (client as any)?.baseline_ar_total || 0
        const newBaseline = currentBaseline + importedTotal

        console.log(`Current baseline: ${currentBaseline}, Adding: ${importedTotal}, New baseline: ${newBaseline}`)

        // Update client's baseline_ar_total
        const { error } = await supabase
          .from('clients')
          .update({ baseline_ar_total: newBaseline } as never)
          .eq('id', clientId)

        if (error) {
          console.error('Failed to update baseline AR:', error)
        } else {
          console.log(`✓ Updated baseline AR for client ${clientId}: ${newBaseline}`)
        }
      } catch (error) {
        console.error('Failed to update baseline AR:', error)
      }
    }

    setImportResults({ success, failed })
    setImportComplete(true)
    setImporting(false)
  }

  const downloadTemplate = () => {
    const template = [
      ['client_name', 'customer_name', 'invoice_number', 'invoice_date', 'due_date', 'original_amount', 'status'],
      ['Example Client', 'Example Customer', 'INV-001', '2026-01-01', '2026-01-31', '1000.00', 'outstanding'],
    ]
    const csv = Papa.unparse(template)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'invoice_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const validCount = parsed.filter(r => r.valid).length
  const invalidCount = parsed.filter(r => !r.valid).length

  const columns = [
    { key: 'row', header: 'Row' },
    {
      key: 'valid',
      header: 'Status',
      render: (r: ValidationResult) => r.valid ? (
        <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Valid</Badge>
      ) : (
        <Badge variant="danger"><AlertCircle className="h-3 w-3 mr-1" />Invalid</Badge>
      )
    },
    { key: 'client_name', header: 'Client', render: (r: ValidationResult) => r.data.client_name },
    { key: 'customer_name', header: 'Customer', render: (r: ValidationResult) => r.data.customer_name },
    { key: 'invoice_number', header: 'Invoice #', render: (r: ValidationResult) => r.data.invoice_number },
    { key: 'amount', header: 'Amount', render: (r: ValidationResult) => formatCurrency(parseFloat(r.data.original_amount || '0')) },
    {
      key: 'errors',
      header: 'Errors',
      render: (r: ValidationResult) => r.errors.length > 0 ? (
        <ul className="text-xs text-red-600">
          {r.errors.map((err, i) => <li key={i}>{err}</li>)}
        </ul>
      ) : '—'
    },
  ]

  if (importComplete) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/invoices')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Import Complete</h1>
              <p className="text-sm text-gray-600">Invoice import results</p>
            </div>
          </div>
        </div>

        <Card>
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Import Complete</h2>
            <p className="text-gray-600 mb-6">
              Successfully imported {importResults.success} invoices
              {importResults.failed > 0 && `, ${importResults.failed} failed`}
            </p>
            <div className="flex justify-center space-x-4">
              <Button onClick={() => navigate('/invoices')}>View Invoices</Button>
              <Button variant="secondary" onClick={() => {
                setFile(null)
                setParsed([])
                setImportComplete(false)
                setImportResults({ success: 0, failed: 0 })
              }}>
                Import Another File
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Import Invoices</h1>
            <p className="text-sm text-gray-600">Bulk upload invoices from CSV</p>
          </div>
        </div>
        <Button variant="secondary" onClick={downloadTemplate}>
          <Download className="h-4 w-4 mr-2" />Download Template
        </Button>
      </div>

      <Card className="mb-6 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <Users className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Need to import customers first?</h3>
              <p className="text-sm text-blue-700">
                Make sure your customers exist before importing invoices. Import customers first to avoid validation errors.
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate('/customers/import')} className="whitespace-nowrap">
            Import Customers
          </Button>
        </div>
      </Card>

      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Upload CSV File</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-600 mb-4">
            Drop a CSV file here or click to browse
          </p>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
          />
          <label htmlFor="csv-upload" className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors cursor-pointer">
            Select File
          </label>
          {file && (
            <p className="text-sm text-gray-600 mt-4">
              Selected: <span className="font-medium">{file.name}</span>
            </p>
          )}
        </div>
      </Card>

      {parsed.length > 0 && (
        <>
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Validation Results</h2>
                <p className="text-sm text-gray-600">
                  {validCount} valid, {invalidCount} invalid
                </p>
              </div>
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || importing}
                loading={importing}
              >
                Import {validCount} Invoices
              </Button>
            </div>
          </Card>

          <Card>
            <DataTable
              data={parsed}
              columns={columns}
              emptyMessage="No data parsed"
            />
          </Card>
        </>
      )}
    </div>
  )
}
